import { createSelector } from "@reduxjs/toolkit";
import type { ApiCarrierDetailUser } from "api-client";
import type { RootState } from "../store";

const selectCarrierDetailState = (state: RootState) => state.carrierDetail;

export const selectCarrierDetail = createSelector(
  [selectCarrierDetailState],
  (state) => state.carrier,
);

export const selectCarrierDetailUsers = createSelector([selectCarrierDetail], (carrier) => {
  const list = carrier?.users;
  return Array.isArray(list) ? list : ([] as ApiCarrierDetailUser[]);
});

export const selectCarrierDetailLoading = createSelector(
  [selectCarrierDetailState],
  (state) => state.loading,
);

export const selectCarrierDetailError = createSelector(
  [selectCarrierDetailState],
  (state) => state.error,
);

export const selectCarrierDriversList = createSelector(
  [selectCarrierDetailState],
  (state) => state.driversList,
);

export const selectCarrierDriversRows = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.rows,
);

export const selectCarrierDriversTotal = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.totalCount,
);

export const selectCarrierDriversLoading = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.loading,
);

export const selectCarrierDriversError = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.error,
);

export const selectCarrierDriversFromIndex = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.fromIndex,
);

export const selectCarrierDriversSearch = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.search,
);

export const selectCarrierDriversShowOnlyActive = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.showOnlyActive,
);

export const selectCarrierDriversSortField = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.sortField,
);

export const selectCarrierDriversSortOrder = createSelector(
  [selectCarrierDriversList],
  (driversList) => driversList.sortOrder,
);

export const selectCarrierCompliance = createSelector(
  [selectCarrierDetailState],
  (state) => state.compliance,
);

export const selectCarrierComplianceData = createSelector(
  [selectCarrierCompliance],
  (compliance) => compliance.data,
);

export const selectCarrierComplianceLoading = createSelector(
  [selectCarrierCompliance],
  (compliance) => compliance.loading,
);

export const selectCarrierEquipmentList = createSelector(
  [selectCarrierDetailState],
  (state) => state.equipmentList,
);

export const selectCarrierEquipmentRows = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.rows,
);

export const selectCarrierEquipmentTotal = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.totalCount,
);

export const selectCarrierEquipmentLoading = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.loading,
);

export const selectCarrierEquipmentError = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.error,
);

export const selectCarrierEquipmentFromIndex = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.fromIndex,
);

export const selectCarrierEquipmentSearch = createSelector(
  [selectCarrierEquipmentList],
  (equipmentList) => equipmentList.search,
);

export const selectCarrierBookingsList = createSelector(
  [selectCarrierDetailState],
  (state) => state.bookingsList,
);

export const selectCarrierBookingsRows = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.rows,
);

export const selectCarrierBookingsTotal = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.totalCount,
);

export const selectCarrierBookingsLoading = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.loading,
);

export const selectCarrierBookingsError = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.error,
);

export const selectCarrierBookingsFromIndex = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.fromIndex,
);

export const selectCarrierBookingsSortOrder = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.sortingOrder,
);

export const selectCarrierBookingsSortField = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.sortingField,
);

export const selectCarrierBookingsStatusFilterValues = createSelector(
  [selectCarrierBookingsList],
  (bookingsList) => bookingsList.statusFilterValues,
);
